import React, {useEffect, useState} from "react"
import {Container, Typography, Paper, Grid, CircularProgress} from "@mui/material"
import {Order} from "../../dtos/orderResponse"
import OrderDetails from "./OrderDetails"
import OrderService from "../../services/order/OrderService"
import CircularIndeterminate from "../feedback/CircularIndeterminate"

import {ActionReasonsContext} from "../../contexts/ActionReasonsContext"
import PaginationWrapper from "../paging/PaginationWrapper"
import _ from "lodash"
import {makeStyles} from "tss-react/mui"
import {useParams} from "react-router-dom"
const useStyles = makeStyles()({
	loading: {
		size: "40",
		left: "-20",
		marginLeft: "50%",
		alignContent: "center",
		paddingTop: "50px",
	},
	loadingPaging: {
		size: "10",
	},
	orders: {
		paddingBottom: "50px",
	},
	noOrders: {
		padding: "15px",
		margin: "15px",
		textAlign: "center",
	},
})

interface OrdersProps {
	customerId: string | null
}

// interface OrderRouterProps {
// 	orderNumber: string
// }

export default function Orders(props: OrdersProps) {
	const {customerId} = props
	const params = useParams()
	const orderNumber = params.orderNumber ?? ""

	const {classes} = useStyles()
	const [loadingInitialOrders, setLoadingInitialOrders] = useState(false)
	const [loadingRemainingOrders, setLoadingRemainingOrders] = useState(false)

	const [orders, setOrders] = useState<Array<Order>>([])
	const [reasons, setReasons] = useState<Array<string>>([])
	const [expandItems, setExpandItems] = useState(false)

	const [currentPage, setCurrentPage] = useState<number>(1)
	const [ordersPerPage, setOrdersPerPage] = useState<number>(10)

	const indexOfLastPage: number = currentPage * ordersPerPage
	const indexOfFirstPage: number = indexOfLastPage - ordersPerPage
	const totalPagesNumber = Math.ceil(orders.length / ordersPerPage)
	const handlePageOnChange = (newPage: number) => {
		newPage > totalPagesNumber ? setCurrentPage(totalPagesNumber) : setCurrentPage(newPage)
	}

	const handleOrdersPerPage = (docsPerPage: number) => {
		setOrdersPerPage(docsPerPage)
		setCurrentPage(1)
	}

	useEffect(() => {
		const svc = new OrderService()
		async function loadReasons() {
			const reasons = await svc.getReasons()
			setReasons(reasons)
		}
		loadReasons()
	}, [])

	useEffect(() => {
		async function loadOrders(customerId: string, orderNumber: string | null) {
			const svc = new OrderService()

			if (orderNumber) {
				const order = await svc.getOrder(orderNumber)
				if (order) {
					setOrders([order])
					setExpandItems(true)
				} else {
					console.log("unable to find order")
				}
				setLoadingInitialOrders(false)
			} else if (customerId) {
				let orders = await svc.getPagedOrders(customerId, 0, 10)
				setOrders(orders)
				setLoadingInitialOrders(false)
				if (orders.length === 10) {
					setLoadingRemainingOrders(true)
					orders = await svc.getAllOrders(customerId, 0, [])
					setLoadingRemainingOrders(false)
					setOrders(orders)
				}
			}
		}
		setLoadingInitialOrders(true)
		customerId && loadOrders(customerId, orderNumber)
	}, [customerId, orderNumber])

	return loadingInitialOrders ? (
		<div className={classes.loading}>
			<CircularIndeterminate />
		</div>
	) : orders.length <= 0 ? (
		<Paper className={classes.noOrders}>
			<Typography>No Orders</Typography>
		</Paper>
	) : (
		<ActionReasonsContext.Provider value={{reasons}}>
			<Container maxWidth={false} className={classes.orders}>
				{!orderNumber && (
					<Grid container direction="row" style={{margin: "10px"}}>
						<Grid item xs={4} style={{alignSelf: "center"}}>
							<PaginationWrapper
								currentPage={currentPage}
								elementsPerPage={ordersPerPage}
								pagesNumber={totalPagesNumber}
								handleElementsPerPage={(ordersTotalPerPage: number) => {
									handleOrdersPerPage(ordersTotalPerPage)
								}}
								handlePageChange={(newPage: number) => {
									handlePageOnChange(newPage)
								}}
							/>
						</Grid>
						{loadingRemainingOrders && (
							<Grid container item xs={8} style={{alignSelf: "center"}}>
								<Grid item style={{alignSelf: "center"}}>
									<CircularProgress style={{width: "30px", height: "30px", margin: "5px"}} />
								</Grid>
								<Grid item style={{alignSelf: "center", marginLeft: "15px"}}>
									<Typography variant="h5" display="block" color="textPrimary">
										Loading the remaining orders
									</Typography>
								</Grid>
							</Grid>
						)}
					</Grid>
				)}
				{_.times(ordersPerPage, (index: number) => {
					let indexOfCurrentOrder: number = index + indexOfFirstPage
					if (orders[indexOfCurrentOrder])
						return (
							<OrderDetails
								key={orders[indexOfCurrentOrder].orderNumber}
								customerId={customerId ? customerId : ""}
								order={orders[indexOfCurrentOrder]}
								expandItems={expandItems}
							/>
						)
				})}
			</Container>
		</ActionReasonsContext.Provider>
	)
}
